import React, { useRef, useEffect, useCallback } from 'react';
import { Form, Field, FormSpy } from 'react-final-form';
import { useIntl } from 'react-intl';
import _find from 'lodash/find';
import _isEmpty from 'lodash/isEmpty';
import classnames from 'classnames';
import {
  CUSTOM_FIELD_STATUS_YES,
  CUSTOM_FIELD_STATUS_CONDITIONED,
  CUSTOM_FIELD_TYPE_BOOLEAN,
  CUSTOM_FIELD_TYPE_FILE,
  CUSTOM_FIELD_TYPE_NUMERIC,
  CUSTOM_FIELD_TYPE_PHONE_NUMBER,
  CUSTOM_FIELD_TYPE_TEXT
} from '../../constants/backend';
import { connect, useDispatch } from 'react-redux';
import { setMemberCustomFieldsFormValues } from '../../actions/customFields';
import { PureFileField } from './fields/file';
import { PureTextField } from './fields/text';
import { PureBoolField } from './fields/bool';
import {
  localeSelector,
  selectFieldIdsByPosition,
  messagesSelector
} from '../../helpers/selectors';
import { isEmptyField, strToBool } from '../../utils/utils';
import { scrollToErrorInfo } from '../../helpers/scrollToError';
import { customFieldIsVisible } from '../../helpers/components';

function fieldIsMandatory(field, fieldValues, fieldIdsByPosition) {
  const { companyCustomField } = field || {};
  const { mandatory, mandatoryCondition } = companyCustomField || {};
  const { dependsOnPosition, expectedValueForPosition } = mandatoryCondition || {};

  if (mandatory === CUSTOM_FIELD_STATUS_CONDITIONED) {
    const parentId = fieldIdsByPosition[dependsOnPosition];
    const parentValue = !!fieldValues[parentId];

    return parentValue === strToBool(expectedValueForPosition);
  } else return mandatory === CUSTOM_FIELD_STATUS_YES;
}

function getFieldId(field) {
  const { companyCustomField } = field || {};
  const { id } = companyCustomField || {};

  return id;
}

function setRangeErrors(field, values, errors, intl) {
  const fieldId = getFieldId(field);
  const fieldValue = values[fieldId];
  const textLength = fieldValue.length;
  const { formatMessage } = intl;
  const { companyCustomField } = field || {};
  const { max, min, fieldType } = companyCustomField || {};

  if (fieldType === CUSTOM_FIELD_TYPE_TEXT) {
    if (textLength < min) {
      errors[fieldId] = formatMessage({ id: 'error.text.length.short' }, { count: min });
    } else if (textLength > max) {
      errors[fieldId] = formatMessage({ id: 'error.text.length.long' }, { count: max });
    }
  } else if (fieldType === CUSTOM_FIELD_TYPE_NUMERIC) {
    const number = Number(fieldValue);

    if (number < min) {
      errors[fieldId] = formatMessage({ id: 'error.number.below.min' }, { value: min });
    } else if (number > max) {
      errors[fieldId] = formatMessage({ id: 'error.number.above.max' }, { value: max });
    }
  }
}

/**
 * @typedef {object} CustomFieldsFormProps
 * @property {(submit: Function) => any} [passFormSubmit]
 * @property {Array} [customFields]
 * @property {Array} [fields]
 */
function CustomFieldsForm({
  customFields = [],
  initialValues,
  passFormSubmit,
  locale,
  fieldIdsByPosition,
  m
}) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const formApiRef = useRef(null);
  const validate = useCallback(
    (values = {}) => {
      const errors = {};

      customFields.forEach((field) => {
        const visible = customFieldIsVisible(field, values, fieldIdsByPosition);

        if (visible) {
          const fieldId = getFieldId(field);
          const { companyCustomField: ccf } = field || {};
          const fieldValue = values[fieldId];
          const emptyField = isEmptyField(fieldValue, ccf?.fieldType);

          if (emptyField) {
            const mandatory = fieldIsMandatory(field, values, fieldIdsByPosition);

            if (mandatory) errors[fieldId] = true;
          } else setRangeErrors(field, values, errors, intl);
        }
      });
      return errors;
    },
    [customFields, fieldIdsByPosition, intl]
  );

  useEffect(() => {
    if (passFormSubmit) {
      passFormSubmit((action) => {
        const values = formApiRef.current?.getState().values || {};
        const errors = validate(values);

        if (_isEmpty(errors)) {
          action(values);
        } else {
          // marks all fields as touched so errors become visible
          formApiRef.current?.submit();
          scrollToErrorInfo();
        }
      });
    }
  }, [passFormSubmit, validate]);

  function selectCurrentLabel(companyCustomField = {}) {
    const { companyCustomFieldLabels, name } = companyCustomField;
    const found = _find(
      companyCustomFieldLabels,
      (label = {}) => label.language === locale
    );
    let { label } = found || {};

    if (!label) {
      const engPredicate = (label = {}) => label.language === 'en';
      const frPredicate = (label = {}) => label.language === 'fr';
      const eng = _find(companyCustomFieldLabels, engPredicate);
      const fr = _find(companyCustomFieldLabels, frPredicate);
      const { label: engLabel } = eng || {};
      const { label: frLabel } = fr || {};

      label = engLabel || frLabel;
    }
    return label || name;
  }

  function selectFieldType(field, values) {
    const { companyCustomField } = field || {};
    const { fieldType, id } = companyCustomField || {};
    const label = selectCurrentLabel(companyCustomField);
    const mandatory = fieldIsMandatory(field, values, fieldIdsByPosition);
    const name = id;

    if (fieldType === CUSTOM_FIELD_TYPE_FILE) {
      return <Field key={id} name={name} label={label} mandatory={mandatory} component={PureFileField} />;
    } else if (fieldType === CUSTOM_FIELD_TYPE_NUMERIC) {
      return (
        <Field
          key={id}
          name={name}
          type="number"
          label={label}
          mandatory={mandatory}
          placeholder={m['placeholder.enter.number']}
          component={PureTextField}
        />
      );
    } else if (
      fieldType === CUSTOM_FIELD_TYPE_TEXT ||
      fieldType === CUSTOM_FIELD_TYPE_PHONE_NUMBER
    ) {
      return (
        <Field
          key={id}
          name={name}
          label={label}
          mandatory={mandatory}
          placeholder={m['placeholder.enter.text']}
          component={PureTextField}
        />
      );
    } else if (fieldType === CUSTOM_FIELD_TYPE_BOOLEAN) {
      return <Field key={id} name={name} type="checkbox" label={label} mandatory={mandatory} component={PureBoolField} />;
    }
  }

  function customField(field, values) {
    if (customFieldIsVisible(field, values, fieldIdsByPosition)) {
      return selectFieldType(field, values);
    }
  }

  return (
    <Form onSubmit={() => {}} initialValues={initialValues} validate={validate}>
      {({ form, values }) => {
        formApiRef.current = form;
        return (
          <form
            className={classnames('custom-fields-form', 'scope-section')}
            onSubmit={(e) => e.preventDefault()}
            noValidate
          >
            <FormSpy
              subscription={{ values: true }}
              onChange={({ values: spyValues }) =>
                dispatch(setMemberCustomFieldsFormValues(spyValues))
              }
            />
            <div className="scope-section-title pb-xs-0 pt-dt-0" />
            <div className="scope-section-fields">
              {customFields.map((field) => customField(field, values))}
            </div>
          </form>
        );
      }}
    </Form>
  );
}

function mapStateToProps(state, props) {
  const { customFields } = props;

  return {
    locale: localeSelector(state),
    fieldIdsByPosition: selectFieldIdsByPosition(customFields),
    m: messagesSelector(state)
  };
}

export default connect(mapStateToProps)(CustomFieldsForm);
